export type Unsubscribe = () => void;

export interface EmitOptions {
  // Indica si este evento está siendo sincronizado desde el historial.
  sync?: boolean;
}

export interface EventPayload<T> {
  data?: T;
  options?: EmitOptions;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createEventBus<TEvents extends Record<string, any>>() {
  const target = new EventTarget();

  function emit<K extends keyof TEvents & string>(
    type: K,
    detail?: TEvents[K],
    emitOptions?: EmitOptions
  ) {
    const payload: EventPayload<TEvents[K]> = {
      data: detail,
      options: emitOptions,
    };
    target.dispatchEvent(new CustomEvent(type, { detail: payload }));
  }

  // Overload: single
  function on<K extends keyof TEvents & string>(
    type: K,
    listener: (payload: TEvents[K], emitOptions?: EmitOptions) => void,
    listenerOptions?: AddEventListenerOptions
  ): Unsubscribe;

  // Overload: multiple
  function on<K extends keyof TEvents & string>(
    types: K[],
    listener: (payload: TEvents[K], emitOptions?: EmitOptions) => void,
    listenerOptions?: AddEventListenerOptions
  ): Unsubscribe;

  // Implementation
  function on<K extends keyof TEvents & string>(
    typeOrTypes: K | K[],
    listener: (payload: TEvents[K], emitOptions?: EmitOptions) => void,
    listenerOptions?: AddEventListenerOptions
  ): Unsubscribe {
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];
    const unsubscribers: Unsubscribe[] = [];

    types.forEach(type => {
      const wrapped = (e: Event) => {
        const eventPayload = (e as CustomEvent<EventPayload<TEvents[K]>>)
          .detail;
        listener(eventPayload.data!, eventPayload.options);
      };
      target.addEventListener(type, wrapped, listenerOptions);
      unsubscribers.push(() =>
        target.removeEventListener(type, wrapped, listenerOptions)
      );
    });

    return () => unsubscribers.forEach(unsub => unsub());
  }

  return { emit, on };
}
